import { Radio, RadioGroup } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { errorTextCls } from "components/tw/classes";
import DatePicker from "features/datepicker/date-picker";
import { vilniusYmd } from "utils/vilnius-date";

/**
 * How long a contract runs. `endDate` is kept while "indefinite" is chosen, so
 * switching back to a fixed term finds the date again; it only counts when
 * `isFixed` is true.
 */
export type ContractTerm = { isFixed: boolean; endDate: string };

/** A stored end date (`YYYY-MM-DD`, "" when there is none) as the term it means. */
export function termFromEndDate(endDate: string): ContractTerm {
  return { isFixed: !!endDate, endDate };
}

/** The end date the contract really has: "" for an indefinite one. */
export function endDateOf(term: ContractTerm): string {
  return term.isFixed ? term.endDate : "";
}

/** A fixed term was chosen but no end date given yet. */
export function isContractTermIncomplete(term: ContractTerm): boolean {
  return term.isFixed && !term.endDate;
}

type Props = {
  value: ContractTerm;
  onChange: (next: ContractTerm) => void;
  /** Shown under the field, e.g. "choose the end date". */
  error?: string;
  readOnly?: boolean;
};

const optionCls =
  "group flex cursor-pointer items-start gap-3 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";
const dotCls =
  "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-zinc-400 group-data-[checked]:border-indigo-600 group-data-[checked]:bg-indigo-600 group-data-[focus]:ring-2 group-data-[focus]:ring-indigo-500/30";

function TermOption({ value, label, help }: { value: string; label: string; help?: string }) {
  return (
    <Radio value={value} className={optionCls}>
      <span className={dotCls}>
        <span className="size-1.5 rounded-full bg-white opacity-0 group-data-[checked]:opacity-100" />
      </span>
      <span>
        <span className="block text-sm font-medium text-zinc-800 dark:text-zinc-100">{label}</span>
        {help ? <span className="block text-xs text-zinc-500">{help}</span> : null}
      </span>
    </Radio>
  );
}

/**
 * "Neterminuota sutartis" is a choice of its own, not an empty date box: the
 * date picker appears only when a fixed term is chosen.
 */
export default function ContractTermField({
  value,
  onChange,
  error,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <RadioGroup
        value={value.isFixed ? "fixed" : "indefinite"}
        onChange={(kind: string) => onChange({ ...value, isFixed: kind === "fixed" })}
        disabled={readOnly}
        aria-label={t("contract_term")}
        className="space-y-3"
      >
        <TermOption
          value="indefinite"
          label={t("contract_term_indefinite")}
          help={t("contract_term_indefinite_help")}
        />
        <TermOption value="fixed" label={t("contract_term_fixed")} />
      </RadioGroup>

      {value.isFixed && (
        <DatePicker
          disableDatesAfter={null}
          title={t("contract_term_end_date")}
          isRequired={false}
          onChange={(date: Date | string | null) =>
            onChange({ ...value, endDate: date ? vilniusYmd(date) : "" })
          }
          selected={value.endDate || null}
          readOnly={readOnly}
        />
      )}
      {error ? <p className={errorTextCls}>{error}</p> : null}
    </div>
  );
}
